import { useState } from 'react'

import { FiltersDefinition, FiltersState, IconType } from '@factorialco/f0-react'
import { DateRange, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { isEqual } from 'lodash'

import { ColumnFormatHint } from 'modules/ai_reports/components/DatasourceTypeRenderer/utils'
import { ReportSchemaType } from 'modules/ai_reports/lib/types'

import SnapshotTableContent from './SnapshotTableContent'

interface ReportTableAction {
  label: string
  icon: IconType
  onClick: () => void
}

interface ReportTableProps {
  id: string
  reportName: string
  sqlQuery: string
  actions?: {
    primary?: ReportTableAction
    secondary?: ReportTableAction[]
  }
  columnFormatHints: ColumnFormatHint[]
  onStateChange: React.ComponentProps<typeof OneDataCollection>['onStateChange']
  tableId: string
  dateFilter?: DateRange
  onUserFacingSqlQueryChange: (sqlQuery: string) => void
  currentFilters?: FiltersState<FiltersDefinition<string>>
  schema: ReportSchemaType
}

const ReportTable = ({
  id,
  sqlQuery,
  actions,
  columnFormatHints,
  onStateChange,
  tableId,
  dateFilter,
  onUserFacingSqlQueryChange,
  currentFilters,
  schema,
}: ReportTableProps) => {
  /*
   * NOTE: Schemas are difficult to manage.
   * Schema is necessary for different things, one is showing the appropriate
   * columns, another one is building the available filters.
   * The problem is that there's not just one schema but two: one is the schema
   * of the table, and another one is the schema of the query.
   * Both are important: for the available columns you need the query schema,
   * for the available filters you need a merge of both, and what's worse,
   * they might overlap.
   *
   * Currently we don't do any attempts to do the merge. Furthermore, there's a
   * problem in the Data Collection where changing the available filters will
   * not be reflected in the UI.
   */
  const [currentSchema, setCurrentSchema] = useState<ReportSchemaType>(schema)
  const handleSetCurrentSchema = (receivedSchema: ReportSchemaType) => {
    // Prevent infinite re-fetches of the data collection
    if (!isEqual(receivedSchema, currentSchema)) setCurrentSchema(receivedSchema)
  }

  return (
    <SnapshotTableContent
      id={id}
      sqlQuery={sqlQuery}
      tableId={tableId}
      actions={actions}
      columnFormatHints={columnFormatHints}
      onStateChange={onStateChange}
      dateFilter={dateFilter}
      onUserFacingSqlQueryChange={onUserFacingSqlQueryChange}
      currentFilters={currentFilters}
      currentSchema={currentSchema}
      onSchemaChanged={handleSetCurrentSchema}
    />
  )
}

export default ReportTable
export { type ReportTableAction, type ReportTableProps }
